import React, { useState, useEffect } from 'react';
import { fetchWorkOrders } from './api'; // Adjust the import path as necessary
import './archived.css';
import WorkOrderModal from './workordermodal';
import NewWorkOrderModal from './newworkordermodal';
import PaymentBadges from './PaymentBadges';

const Archived = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState('All');
  const [selectedDate, setSelectedDate] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showNewWorkOrderModal, setShowNewWorkOrderModal] = useState(false);
  const [sortByDatePromised, setSortByDatePromised] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState({});

  // Format date without year for mobile view
  const formatDateWithoutYear = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}`;
  };

  const toggleNewWorkOrderModal = () => {
    setShowNewWorkOrderModal(!showNewWorkOrderModal);
  };

  const handleWorkOrderCreated = (newWorkOrder) => {
    setWorkOrders([...workOrders, newWorkOrder]);
    setShowNewWorkOrderModal(false);
  };

  const toggleService = (service) => {
    setSelectedService(service);
  };

  const toggleDate = (date) => {
    setSelectedDate(date);
  };

  const toggleStatus = (status) => {
    setSelectedStatus(selectedStatus === status ? '' : status);
  };

  const toggleSortByDatePromised = () => {
    setSortByDatePromised(!sortByDatePromised);
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleWorkOrderClick = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setShowModal(true);
  };

  const handleWorkOrderUpdate = (updatedWorkOrder) => {
    // If work order is no longer archived, remove it from the archived list
    if (!updatedWorkOrder.is_archived) {
      setWorkOrders(workOrders.filter(wo => wo.id !== updatedWorkOrder.id));
    } else {
      // If work order is still archived, update it in the list
      setWorkOrders(workOrders.map(wo => wo.id === updatedWorkOrder.id ? updatedWorkOrder : wo));
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (paginationInfo.hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.hasPrevious) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    const loadWorkOrders = async () => {
      // Convert "All" to empty string for API call
      const serviceFilter = selectedService === 'All' ? '' : selectedService;
      const dateFilter = selectedDate === 'All' ? '' : selectedDate;
      const fetchedWorkOrders = await fetchWorkOrders(serviceFilter, dateFilter, searchQuery, selectedStatus, true, currentPage, 50);

      if (fetchedWorkOrders && fetchedWorkOrders.results) {
        setWorkOrders(fetchedWorkOrders.results);
        setTotalPages(fetchedWorkOrders.total_pages || 1);
        setTotalCount(fetchedWorkOrders.count || 0);
        setPaginationInfo({
          hasNext: fetchedWorkOrders.has_next || false,
          hasPrevious: fetchedWorkOrders.has_previous || false,
          nextPage: fetchedWorkOrders.next_page || null,
          previousPage: fetchedWorkOrders.previous_page || null
        });
      } else {
        // Handle case where response doesn't have expected structure
        setWorkOrders([]);
        setTotalPages(1);
        setTotalCount(0);
        setPaginationInfo({
          hasNext: false,
          hasPrevious: false,
          nextPage: null,
          previousPage: null
        });
      }
    };

    loadWorkOrders();
  }, [selectedService, selectedDate, searchQuery, selectedStatus, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedService, selectedDate, searchQuery, selectedStatus]);

  return (
    <div className='aworkorderlist'>
      <div className='aworkorderlistfilters'>
        <button className='anewworkorderbutton' onClick={toggleNewWorkOrderModal}>
          <span className='anewworkorderbuttontext'>New Work Order</span>
          <span className='anewworkorderbuttonplus'>+</span>
        </button>
        <div className='aworkorderlistfiltersrow'>
          <div className='astatusfilter'>
            <label className='astatuslabel'>
              <span className='aworkorderlistlabeltext'>Status:</span>
              <select className='astatusselect' value={selectedStatus} onChange={(e) => toggleStatus(e.target.value)}>
                <option value="">All</option>
                <option value="In Process">In Process</option>
                <option value="Called">Called</option>
                <option value="Picked Up">Picked Up</option>
              </select>
            </label>
          </div>
          <div className='astatusfilter'>
            <label className='astatuslabel'>
              <span className='aworkorderlistlabeltext'>Service:</span>
              <select className='astatusselect' value={selectedService} onChange={(e) => toggleService(e.target.value)}>
                <option value="All">All</option>
                <option value="Restoration">Restoration</option>
                <option value="Straightening">Straightening</option>
                <option value="Repair Crack">Repair Crack</option>
                <option value="Powder Coating">Powder Coating</option>
                <option value="Mount & Balance">Mount & Balance</option>
                <option value="Other">Other</option>
              </select>
            </label>
          </div>
          <div className='astatusfilter'>
            <label className='astatuslabel'>
              <span className='aworkorderlistlabeltext'>Date:</span>
              <select className='astatusselect' value={selectedDate} onChange={(e) => toggleDate(e.target.value)}>
                <option value="All">All</option>
                <option value="today">Today</option>
                <option value="tomorrow">Tomorrow</option>
              </select>
            </label>
          </div>

          <button className='asortButton' onClick={toggleSortByDatePromised}>
            {sortByDatePromised ? 'Due X' : 'Due'}
          </button>
        </div>
        <label className='astatuslabel aworkorderlistsearchlabel'>
          <span className='aworkorderlistlabeltext'>Search:</span>
          <input
            className='asearchbar'
            type="text"
            placeholder="⌕ Search Work Orders"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </label>
      </div>
      <button className='aworkorderlistmobilebutton' onClick={toggleNewWorkOrderModal}>
        <span className='anewworkorderbuttonplus'>+</span>
      </button>
      <div className='aworkorderlistlabelsparent'>
        <div className='aworkorderlistlabels'>
          <h5 className='aworkorderlistlabelsheaders'>
            <span className='aworkorderlistlabeldesktop'>Customer Name</span>
            <span className='aworkorderlistlabelmobile'>Customer</span>
          </h5>
          <h5 className='aworkorderlistlabelsheaders'>
            <span className='aworkorderlistlabeldesktop'>Vehicle Make</span>
            <span className='aworkorderlistlabelmobile'>Vehicle</span>
          </h5>
          <h5 className='aworkorderlistlabelsheaders'>Service</h5>
          <h5 className='aworkorderlistlabelsheaders'>
            <span className='aworkorderlistlabeldesktop'>Date Promised</span>
            <span className='aworkorderlistlabelmobile'>Due</span>
          </h5>
          <h5 className='aworkorderlistlabelsheaders'>
            <span className='aworkorderlistlabeldesktop'>Work Order #</span>
            <span className='aworkorderlistlabelmobile'>WO #</span>
          </h5>
        </div>
        <hr className='aworkorderlistlabelsheadersline'/>
      </div>
      <div className='aworkorderlistscroll'>
        {workOrders && workOrders.length > 0 ? (
          workOrders
            .slice() // Create a shallow copy of the array
            .sort((a, b) => {
              if (sortByDatePromised) {
                // Sort by datePromised in ascending order
                return new Date(a.date_promised) - new Date(b.date_promised);
              }
              // Default to sorting by created_at in descending order
              return new Date(b.created_at) - new Date(a.created_at);
            })
            .map((order) => (
              <div className='aworkorderlistrow' key={order.id} onClick={() => handleWorkOrderClick(order)}>
                <div className='aworkorderlistrowdataname'>
                  {order.customer_name}
                  <PaymentBadges workOrder={order} />
                </div>
                <div className='aworkorderlistrowdatavehicle'>{order.vehicle}</div>
                <div className='aworkorderlistrowdataservice'>{order.service_requested}</div>
                <div className='aworkorderlistrowdatadate'>
                  <span className='aworkorderlistlabeldesktop'>{order.date_promised}</span>
                  <span className='aworkorderlistlabelmobile'>{formatDateWithoutYear(order.date_promised)}</span>
                </div>
                <div className='aworkorderlistrowdatadate'>{order.id}</div>
              </div>
            ))
        ) : (
          <p>No work orders found</p>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
        <div className="aworkorderlist-pagination">
          <div className="aworkorderlist-pagination-info">
            Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, totalCount)} of {totalCount} archived work orders
          </div>
          <div className="aworkorderlist-pagination-controls">
            <button 
              className="aworkorderlist-pagination-btn"
              onClick={handlePreviousPage}
              disabled={!paginationInfo.hasPrevious}
            >
              ← Previous
            </button>
            
            <div className="aworkorderlist-pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`aworkorderlist-pagination-page ${
                      pageNum === currentPage ? 'active' : ''
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button 
              className="aworkorderlist-pagination-btn"
              onClick={handleNextPage}
              disabled={!paginationInfo.hasNext}
            >
              Next →
            </button>
          </div>
        </div>
        )}
      </div>
      <NewWorkOrderModal
        showModal={showNewWorkOrderModal}
        toggleModal={toggleNewWorkOrderModal}
        onWorkOrderCreated={handleWorkOrderCreated}
      />
      <WorkOrderModal 
        showModal={showModal} 
        toggleModal={toggleModal} 
        workOrder={selectedWorkOrder}
        onWorkOrderUpdated={handleWorkOrderUpdate}
      />
    </div>
  );
};

export default Archived;